import { Component, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { ME_USER } from "app/bundles/authorize/shared/user-profile";
import { ConfirmDialogService } from "app/core/confirm-dialog.service";
import { CoreService } from "app/core/core.service";
import { PreviousPageService } from "app/core/previous-page.service";
import { EMPTY, Subscription, catchError, first, switchMap, tap } from "rxjs";
import { Comment } from "../../requests/shared/models/comment";
import { PhotosGalleryFormComponent } from "../edit-request/photos-gallery-form/photos-gallery-form.component";
import { IMediaPayload } from "../shared/api-services/event-api/event-api.service";
import {
  IAllowableActivity,
  IAllowableResponsible,
} from "../shared/models/currentUser";
import {
  IWorkorderOrServiceRequestSummary,
  RequestSourceTypeEnum,
} from "../shared/models/request";
import { Status } from "../shared/models/status";
import { isInternalWorkorderRequestPipe } from "../shared/pipes/is-internal-workorder-request.pipe";
import { CommentService } from "../shared/services/comment.service";
import { CurrentUserService } from "../shared/services/current-user.service";
import { EditRequestService } from "../shared/services/edit-request.service";
import { IconService } from "../shared/services/icon.service";
import {
  IBatchMode,
  RequestInBatchService,
  UpdateInBatchEnum,
} from "../shared/services/request-in-batch.service";
import { RequestService } from "../shared/services/request.service";
import { StatusService } from "../shared/services/status.service";

interface ISummaryLabel {
  titleSourceLabel?: string;
  detailSourceLabel?: string;
  titleUpdateTypeLabel?: string;
  detailUpdateTypeLabel?: string;
}
@Component({
  selector: "app-summary-in-batch",
  templateUrl: "./summary-in-batch.component.html",
  styleUrls: ["./summary-in-batch.component.scss"],
})
export class SummaryInBatchComponent implements OnInit, OnDestroy {
  public selectedValue = new UntypedFormControl();
  public batchMode: IBatchMode;
  public updateInBatchEnum = UpdateInBatchEnum;
  public requestsInBatch: IWorkorderOrServiceRequestSummary[];
  public allowableUsersList: IAllowableResponsible[];
  public allowableActivitiesList: IAllowableActivity[];
  public formGroup: UntypedFormGroup;
  public subscriptions = new Subscription();
  public summaryLabel: ISummaryLabel = {};
  public isInternalWorkorderRequestPipe = new isInternalWorkorderRequestPipe();
  public meUser = ME_USER;
  public comments: Comment[] = [];
  public statuses: Status[] = [];
  @ViewChild("photosGalleryControl") photosGallery: PhotosGalleryFormComponent;
  @Input()
  redirectRoute: string;

  constructor(
    private requestInBatchService: RequestInBatchService,
    public iconService: IconService,
    public currentUserService: CurrentUserService,
    private fb: UntypedFormBuilder,
    public router: Router,
    private coreService: CoreService,
    private editRequestService: EditRequestService,
    private requestService: RequestService,
    private statusService: StatusService,
    private commentService: CommentService,
    private confirmDialogService: ConfirmDialogService,
    private previousPageService: PreviousPageService,
  ) {
    this.formGroup = this.fb.group({
      selectedValue: [""],
      status: [""],
      reason: [""],
      internalComment: [""],
      photosGallery: [""],
    });
  }

  ngOnInit() {
    this.requestsInBatch =
      this.requestInBatchService.getSelectedRequestsInBatch();
    this.subscriptions.add(
      this.requestInBatchService.batchMode$
        .pipe(
          tap((batchMode) => {
            this.batchMode = batchMode;
            // console.log(`batchMode setted to ${JSON.stringify(this.batchMode)}`);
            this.initItems();
            this.setRequestSourceText();
          }),
        )
        .subscribe(),
    );
  }

  private initItems() {
    if (this.needAssignAllowableActivity()) {
      this.initAllowableActivityItem();
    }

    if (this.needAssignAllowableUser()) {
      this.initAllowableUserItem();
    }

    if (this.needAssignStatus()) {
      this.initAssignStatusItem();
    }

    if (this.needResponseToClient()) {
      this.initResponseToClient();
    }
  }

  private initResponseToClient() {
    this.preprocessReason("T");
    this.subscriptions.add(
      this.formGroup.controls["status"].valueChanges.subscribe((x) => {
        this.preprocessReason(x);
      }),
    );
  }

  private initAssignStatusItem() {
    this.subscriptions.add(
      this.statusService
        .getStatusByCode("T")
        .pipe(tap((status) => this.statuses.push(status)))
        .subscribe(),
    );
    const statusControl: UntypedFormControl = <UntypedFormControl>(
      this.formGroup.controls["status"]
    );
    statusControl.setValue("T");
    statusControl.disable();
  }

  private initAllowableUserItem() {
    this.allowableUsersList =
      this.currentUserService.getAllowableUsersForAGroup(
        this.requestsInBatch?.[0]?.responsible?.groupId,
      );
    this.formGroup?.controls?.selectedValue?.setValidators?.([
      Validators.required,
    ]);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private initAllowableActivityItem(): void {
    this.allowableActivitiesList =
      this.currentUserService.getAllowableActivities4BelongingBorough();
    this.formGroup?.controls?.allowableActivity?.setValidators?.(
      Validators.required,
    );
  }

  private preprocessReason(statusCode: string): void {
    this.subscriptions.add(
      this.commentService
        .getPredefinedTextByStatusCode(statusCode)
        .subscribe((comments) => {
          this.comments = comments.sort((first: Comment, second: Comment) =>
            first.name.localeCompare(second.name),
          );
          const reasonControl: UntypedFormControl = <UntypedFormControl>(
            this.formGroup.controls["reason"]
          );

          if (this.comments.length === 1) {
            reasonControl.setValue(this.comments[0].code);
          } else {
            reasonControl.setValue("");
          }
        }),
    );
  }

  public needAssignStatus() {
    return (
      this.batchMode?.updateType === this.updateInBatchEnum?.closeWorkorder
    );
  }

  public needResponseToClient() {
    return (
      this.batchMode?.updateType === this.updateInBatchEnum?.closeWorkorder
    );
  }

  public needAddPhoto() {
    return (
      this.batchMode?.updateType === this.updateInBatchEnum?.closeWorkorder
    );
  }

  public needInternalComment() {
    return (
      this.batchMode?.updateType === this.updateInBatchEnum?.closeWorkorder
    );
  }

  public needAssignAllowableUser() {
    return [
      this.updateInBatchEnum?.takeInCharge,
      this.updateInBatchEnum?.updateResponsible,
    ].includes(this.batchMode?.updateType);
  }

  public needAssignAllowableActivity() {
    return (
      this.batchMode?.updateType === this.updateInBatchEnum?.updateActivity
    );
  }

  public submit() {
    this.coreService.startSpinner();
    switch (this.batchMode?.updateType) {
      case this.updateInBatchEnum?.takeInCharge:
        this.takeInCharge();
        break;
      case this.updateInBatchEnum?.updateResponsible:
        this.changeResponsible();
        break;
      case this.updateInBatchEnum.updateActivity:
        this.changeActivity();
        break;
      case this.updateInBatchEnum?.closeWorkorder:
        this.closeWorkorder();
        break;
      default:
        break;
    }
  }

  private takeInCharge(): void {
    const selectedValue = this.formGroup.controls.selectedValue.value;
    const requestInBatchIds = this.requestsInBatch.map((request) => request.id);
    this.subscriptions.add(
      this.editRequestService
        .takeInCharge(requestInBatchIds, selectedValue)
        .subscribe({
          next: () => this.reactivateNormalMode(),
          error: () => {
            /* We don't want this error to bubble up to global error handler since already handled in this.editRequestService.takeInCharge */
          },
        }),
    );
  }

  private changeResponsible(): void {
    const selectedValue = this.getSelectedFormValue();
    const requestInBatchIds = this.getRequestInBatchIds();
    this.subscriptions.add(
      this.editRequestService
        .changeResponsible(
          requestInBatchIds,
          selectedValue,
          this.requestsInBatch[0].source,
        )
        .subscribe({
          next: () => this.reactivateNormalMode(),
          error: () => {
            /* We don't want this error to bubble up to global error handler since already handled in this.editRequestService.takeInCharge */
          },
        }),
    );
  }

  private changeActivity() {
    const selectedValue = this.getSelectedFormValue();
    const requestInBatchIds = this.getRequestInBatchIds();
    const externalWorkorderInBatchIds = this.geExternalWorkorderInBatchIds();
    this.subscriptions.add(
      this.editRequestService
        .changeActivity(
          requestInBatchIds,
          this.requestsInBatch[0].source,
          selectedValue,
        )
        .pipe(
          switchMap(() => {
            if (
              this.requestInBatchService.getCurrentRequestSource() ===
                RequestSourceTypeEnum.workorderRequest &&
              externalWorkorderInBatchIds.length > 0
            ) {
              return this.requestService.cancelTakeInCharge(
                externalWorkorderInBatchIds,
              );
            }
            return EMPTY;
          }),
        )
        .subscribe({
          next: () => this.reactivateNormalMode(),
          error: () => {
            /* We don't want this error to bubble up to global error handler since already handled in this.editRequestService.changeActivity */
          },
        }),
    );
  }

  private updateToCloseStatus() {
    const internalComment = this.getInternalCommentFormValue();
    const reason = this.getReasonFormValue();
    const requestIds = this.getRequestInBatchIds();
    const requestType: RequestSourceTypeEnum =
      this.requestInBatchService.getCurrentRequestSource();
    const photos: IMediaPayload[] = this.photosGallery
      ? this.photosGallery.prepareMediaPayloadForBatch()
      : undefined;

    return this.editRequestService.closeWorkorder(
      requestIds,
      requestType,
      reason,
      internalComment,
      photos,
    );
  }

  private autoUnsubscribe() {
    return first();
  }

  private closeWorkorder() {
    this.confirmDialogService.createConfirmDialog({
      confirmedCB: () => {
        this.coreService.startSpinner();
        return this.updateToCloseStatus().pipe(
          tap(() => this.reactivateNormalMode()),
          this.autoUnsubscribe(),
          catchError(() => {
            /* We don't want this error to bubble up since already handled in this.updateToCloseStatus (see this.editRequestService.closeWorkorder) */
            return EMPTY;
          }),
        );
      },
      cancelledCB: async () => {
        null;
      },
    });
  }

  private getRequestInBatchIds(): number[] {
    return this.requestsInBatch.map((request) => request.id);
  }

  private geExternalWorkorderInBatchIds(): number[] {
    return this.requestsInBatch
      .filter(
        (request) => !this.isInternalWorkorderRequestPipe.transform(request),
      )
      .map((res) => res.id);
  }

  private getSelectedFormValue() {
    return this.formGroup.controls.selectedValue.value;
  }

  private getReasonFormValue() {
    return this.formGroup.controls.reason.value;
  }

  private getPhotosGalleryFormValue() {
    return this.formGroup.controls.photosGallery.value;
  }

  private getInternalCommentFormValue() {
    return this.formGroup.controls.internalComment.value;
  }

  public setSelectedActivity(activity: IAllowableActivity) {
    this.formGroup.controls.selectedValue.setValue(activity);
  }

  private setRequestSourceText() {
    if (this.requestsInBatch?.length === 0) {
      return;
    }

    if (
      this.requestsInBatch[0].source === RequestSourceTypeEnum.workorderRequest
    ) {
      this.summaryLabel.titleSourceLabel =
        "components.editRequest.workorderRequest";
      this.summaryLabel.detailSourceLabel =
        "components.editRequest.workorderChosen";
    } else {
      this.summaryLabel.titleSourceLabel =
        "components.editRequest.serviceRequest";
      this.summaryLabel.detailSourceLabel =
        "components.editRequest.serviceRequestChosen";
    }
    switch (this.batchMode?.updateType) {
      case UpdateInBatchEnum.takeInCharge:
        this.summaryLabel.titleUpdateTypeLabel =
          "components.editRequest.takeInCharge";
        this.summaryLabel.detailUpdateTypeLabel =
          "components.editRequest.assignDDS";
        break;
      case UpdateInBatchEnum.updateResponsible:
        this.summaryLabel.titleUpdateTypeLabel =
          "components.editRequest.updateResponsible";
        this.summaryLabel.detailUpdateTypeLabel =
          "components.editRequest.chooseNewResponsible";
        break;
      case UpdateInBatchEnum.updateActivity:
        this.summaryLabel.titleUpdateTypeLabel =
          "components.editRequest.updateActivity";
        this.summaryLabel.detailUpdateTypeLabel =
          "components.editRequest.chooseNewActivity";
        break;
      case UpdateInBatchEnum.closeWorkorder:
        this.summaryLabel.titleUpdateTypeLabel =
          "components.editRequest.closeWorkorder";
        this.summaryLabel.detailUpdateTypeLabel = "";
        break;
      default:
        break;
    }
  }

  private reactivateNormalMode() {
    this.requestInBatchService.desactivateBatchMode();
    void this.router.navigate(["list"]);
  }
}
